import { z } from 'zod';
import { TASK_TYPES, TASK_STATUS, URGENCY_LEVELS, SPECIALTIES } from '../constants/index.js';

const taskTypeEnum = z.enum(Object.values(TASK_TYPES) as [string, ...string[]]);
const taskStatusEnum = z.enum(Object.values(TASK_STATUS) as [string, ...string[]]);
const urgencyEnum = z.enum(URGENCY_LEVELS);
const specialtyEnum = z.enum(SPECIALTIES);

export const createTaskSchema = z.object({
  type: taskTypeEnum,
  // contractId optional — many service types (consultoria, divórcio, orientação
  // preventiva, etc) start without any document attached.
  contractId: z.string().uuid().optional(),
  lawyerId: z.string().uuid().optional(),
  description: z.string().min(20, 'Descreva sua necessidade com pelo menos 20 caracteres').max(5000),
  deadlineDays: z.number().int().min(1).max(60).optional(),
  urgency: urgencyEnum.optional(),
  recommendedSpecialty: specialtyEnum.optional(),
  // When the task was created through AI triage, snapshot the model's
  // classification so admins and lawyers can audit how it was routed.
  triageMeta: z
    .object({
      sourceDescription: z.string().max(5000),
      classifiedAt: z.string().datetime(),
      model: z.string().max(120),
      confidence: z.number().min(0).max(1).optional(),
    })
    .optional(),
});

/** Input the cliente submits to be triaged. */
export const triageInputSchema = z.object({
  description: z
    .string()
    .min(20, 'Descreva sua necessidade com pelo menos 20 caracteres para uma boa classificação')
    .max(5000),
  preferredSpecialty: specialtyEnum.optional(),
  // If the cliente is already viewing a specific lawyer's profile and clicked
  // "Contratar este serviço", we lock the triage to suggest only this lawyer.
  preferredLawyerId: z.string().uuid().optional(),
});

/** AI output, validated server-side before responding. */
export const triageResultSchema = z.object({
  taskType: taskTypeEnum,
  recommendedSpecialty: specialtyEnum,
  urgency: urgencyEnum,
  needsDocument: z.enum(['required', 'optional', 'none']),
  reasoning: z.string().max(2000),
  // What the cliente likely wants in plain Portuguese — used as a starting
  // point for the task `description` field. The cliente can edit before submit.
  suggestedTitle: z.string().max(200),
  // List of follow-up questions if the description is too vague — empty when
  // the model is confident.
  followUpQuestions: z.array(z.string().max(300)).max(5).default([]),
  confidence: z.number().min(0).max(1),
});

export type TriageInput = z.infer<typeof triageInputSchema>;
export type TriageResult = z.infer<typeof triageResultSchema>;

/**
 * Proposta formal do advogado pro cliente. Tem escopo, prazo, preço. Cliente
 * aceita → segue pro checkout. Rejeita → task volta pra ASSIGNED e lawyer pode
 * mandar nova proposta. Hospedada inline em `task.proposal` (não numa coleção
 * separada) porque é 1:1 e o histórico fica no auditlog.
 */
export const taskProposalSchema = z.object({
  scope: z
    .string()
    .min(30, 'Descreva o escopo com pelo menos 30 caracteres')
    .max(3000, 'Escopo muito longo'),
  deadlineDays: z.number().int().min(1).max(180),
  // Cents. Lawyer informa o líquido dele; a plataforma soma 15% no checkout.
  priceAmount: z.number().int().positive().max(10_000_000),
  // Optional message to the cliente — não-binding, contexto.
  message: z.string().max(2000).optional(),
});
export type TaskProposalInput = z.infer<typeof taskProposalSchema>;

export const rejectProposalSchema = z.object({
  reason: z.string().max(500).optional(),
});

export const updateTaskSchema = z.object({
  status: taskStatusEnum.optional(),
  rejectionReason: z.string().max(500).optional(),
});

export const acceptTaskSchema = z.object({
  estimatedDeliveryHours: z.number().int().min(1).max(720),
});

export const taskListQuerySchema = z.object({
  status: taskStatusEnum.optional(),
  type: taskTypeEnum.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export type CreateTaskInput = z.infer<typeof createTaskSchema>;
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;
export type AcceptTaskInput = z.infer<typeof acceptTaskSchema>;
export type TaskListQuery = z.infer<typeof taskListQuerySchema>;
